import React, { useEffect, useRef } from 'react';
import { EventDto } from 'stores/events/events-store';
import { RequestDto } from 'stores/requests/requests-store';

interface WeekdayHeaderProps {
  events: EventDto[];
  currentDate: Date;
  selectedDate: Date | null;
  isBusy: boolean;
  requests: RequestDto[];
  entityType: 'events' | 'requests';
  onSelectDate: (date: Date) => void;
}

const DAY_WIDTH = 64; // w-16 = 4rem = 64px (ajusta si tu width es diferente)
const VISIBLE_DAYS = 7;

const WeekdayHeader: React.FC<WeekdayHeaderProps> = ({ events, currentDate, selectedDate, isBusy, requests, entityType, onSelectDate }) => {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1));

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || !selectedDate) return;
    // Encuentra el índice del día seleccionado
    const selectedIndex = days.findIndex(d => d.toDateString() === selectedDate.toDateString());
    if (selectedIndex === -1) return;
    // Calcula el scroll para centrar el día seleccionado
    const scrollTo = DAY_WIDTH * selectedIndex - DAY_WIDTH * Math.ceil(VISIBLE_DAYS / 1.25);
    containerRef.current.scrollTo({
      left: Math.max(0, scrollTo),
      behavior: 'smooth'
    });
  }, [currentDate, selectedDate, daysInMonth, days]);

  const getEventsForDay = (day: Date) => {
    const dayEvents = events.filter(event => {
      const eventStart = new Date(event.startDate);
      const eventEnd = new Date(event.endDate);
      eventStart.setHours(0, 0, 0, 0);
      eventEnd.setHours(0, 0, 0, 0);
      day.setHours(0, 0, 0, 0);
      return day >= eventStart && day <= eventEnd;
    });

    return (
      <div className={`flex justify-center gap-1 pt-1`}>
        {dayEvents.length > 0 && (
          <div className="flex gap-1">
            {dayEvents.slice(0, 3).map((event, index) => (
              <span
                key={index}
                className="w-1 h-1 rounded-full"
                style={{ backgroundColor: event.eventType?.eventsColor || '#3B82F6' }}
                title={event.title}
              />
            ))}
            {dayEvents.length > 3 && <span className="w-1 h-1 bg-gray-400 rounded-full" title={`+${dayEvents.length - 3} more`} />}
          </div>
        )}
      </div>
    );
  };

  const getRequestsForDay = (day: Date) => {
    const dayRequests = requests.filter(request => {
      const requestStart = new Date(request.startDate);
      const requestEnd = new Date(request.endDate);
      requestStart.setHours(0, 0, 0, 0);
      requestEnd.setHours(0, 0, 0, 0);
      day.setHours(0, 0, 0, 0);
      return day >= requestStart && day <= requestEnd;
    });

    return (
      <div className={`flex justify-center gap-1 pt-1`}>
        {dayRequests.length > 0 && (
          <div className="flex gap-1">
            {dayRequests.slice(0, 3).map((request, index) => (
              <span
                key={index}
                className="w-1 h-1 rounded-full"
                style={{ backgroundColor: request.eventType?.eventsColor || '#3B82F6' }}
                title={request.title}
              />
            ))}
            {dayRequests.length > 3 && <span className="w-1 h-1 bg-gray-400 rounded-full" title={`+${dayRequests.length - 3} more`} />}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`overflow-x-auto whitespace-nowrap flex border-t border-b py-2 px-2 mt-2 
      ${isBusy ? 'opacity-50 pointer-events-none cursor-not-allowed' : ''}`}
      ref={containerRef}
      style={{ scrollBehavior: 'smooth' }}
    >
      {days.map((day, i) => {
        const isSelected = selectedDate?.toDateString() === day?.toDateString();

        return (
          <div
            key={i}
            onClick={() => !isBusy && onSelectDate(day)}
            className={`flex-none w-16 text-center text-sm font-medium rounded p-1 
            ${isSelected ? 'bg-gray-300' : ''}
            ${isBusy ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100'}`}
          >
            <span className={`${isBusy ? 'text-gray-500' : ''}`}>{day.toLocaleDateString('en-US', { weekday: 'short' })}</span>
            <span className={`block text-xs ${isBusy ? 'text-gray-400' : 'text-gray-500'}`}>{day.getDate()}</span>
            {entityType === 'events' ? getEventsForDay(day) : getRequestsForDay(day)}
          </div>
        );
      })}
    </div>
  );
};

export default WeekdayHeader;
